import React from 'react';
import { useSelector } from 'react-redux';
import ActionCard, { ActionCardVariant, ActionCardSize } from 'components/Common/Cards/ActionCard';
import { ReactComponent as AiIcon } from 'images/ai-stars-icon.svg';
import { getFinancialMetricsState } from 'selectors/financialMetrics';
import { VariabilityStatus } from 'components/FinancialCheckup/MetricCard/MetricCard';
import {
  getIncomeVariabilityStatus,
  getExpenseVariabilityStatus,
} from 'components/FinancialCheckup/FinancialDashboard/utils';

import styles from './InsightSection.module.scss';

interface InsightContent {
  title: string;
  description: string;
}

const getInsightContent = (iviStatus: VariabilityStatus, eviStatus: VariabilityStatus): InsightContent => {
  const insightMap: Record<string, Omit<InsightContent, 'variant' | 'iconClass'>> = {
    [`${VariabilityStatus.Steady}-${VariabilityStatus.Steady}`]: {
      title: 'Steady income and spending',
      description:
        "Your income and expenses are steady, showing strong financial rhythm and consistency. We'll factor that in as we help you make steady progress toward your goals.",
    },
    [`${VariabilityStatus.Steady}-${VariabilityStatus.Varies}`]: {
      title: 'Steady income, variable expenses',
      description:
        "Your income is reliable, and both essential and discretionary expenses vary a little over time. We'll keep your plan balanced and realistic.",
    },
    [`${VariabilityStatus.Steady}-${VariabilityStatus.VariesALot}`]: {
      title: 'Steady income, high expense variability',
      description:
        "Your income is predictable, but expenses are quite variable. That's okay. We'll help you manage through the swings and keep your plan flexible.",
    },
    [`${VariabilityStatus.Varies}-${VariabilityStatus.Steady}`]: {
      title: 'Mostly consistent finances',
      description:
        "Your income and expenses are mostly consistent with small changes. We'll use that stability to help you build steady momentum toward your goals.",
    },
    [`${VariabilityStatus.Varies}-${VariabilityStatus.Varies}`]: {
      title: 'Moderate financial variability',
      description:
        "Your income and spending both change a bit each month. We'll help you stay balanced and confident through those normal shifts.",
    },
    [`${VariabilityStatus.Varies}-${VariabilityStatus.VariesALot}`]: {
      title: 'Variable income and expenses',
      description:
        "You see a lot of movement in income and expenses. That's okay—we'll build a plan that bends with your flow, not against it.",
    },
    [`${VariabilityStatus.VariesALot}-${VariabilityStatus.Steady}`]: {
      title: 'Variable income, steady spending',
      description:
        "Your income varies, but your spending is steady. That's a great foundation—we'll tailor your plan around your income rhythm.",
    },
    [`${VariabilityStatus.VariesALot}-${VariabilityStatus.Varies}`]: {
      title: 'High income and expense variability',
      description:
        "Income and expenses both move a lot from month to month. We'll help you stay steady and confident through the changes.",
    },
    [`${VariabilityStatus.VariesALot}-${VariabilityStatus.VariesALot}`]: {
      title: 'Highly variable finances',
      description:
        "Income and all spending types move a lot month to month. That's okay—we're here to help you manage through the variability and stay confident.",
    },
  };

  const content = insightMap[`${iviStatus}-${eviStatus}`];

  return { ...content };
};

const InsightSection: React.FC = () => {
  const { data: financialMetricsData } = useSelector(getFinancialMetricsState);

  if (!financialMetricsData) return null;

  const { ivi, evi } = financialMetricsData;

  if (typeof ivi !== 'number' || typeof evi !== 'number') return null;

  const iviStatus = getIncomeVariabilityStatus(ivi);
  const eviStatus = getExpenseVariabilityStatus(evi);
  const { title, description } = getInsightContent(iviStatus, eviStatus);

  return (
    <div className={styles.insightSection}>
      <ActionCard variant={ActionCardVariant.LightBlue} size={ActionCardSize.Compact}>
        <ActionCard.Row className={styles.cardRow}>
          <ActionCard.Icon className={styles.iconContainer}>
            <AiIcon className={styles.aiIcon} />
          </ActionCard.Icon>
          <ActionCard.Col className={styles.content}>
            <ActionCard.Title className={styles.title}>{title}</ActionCard.Title>
            <ActionCard.Description>{description}</ActionCard.Description>
          </ActionCard.Col>
        </ActionCard.Row>
      </ActionCard>
    </div>
  );
};

export default InsightSection;
